/** First sign-in: choose your own password, then add an authenticator. */
import { useEffect, useRef, useState, type FormEvent } from "react";

import { Card } from "../components/ui";
import { adminApi } from "./api";
import { groupSecret, passwordProblem, setupStep } from "./helpers";
import type { AdminMe, AdminToken, TotpStart } from "./types";

export function AdminSetup({ me, onToken }: { me: AdminMe; onToken: (token: AdminToken) => void }) {
  return setupStep(me) === "password" ? <ChoosePassword onToken={onToken} /> : <AddAuthenticator onToken={onToken} />;
}

function ChoosePassword({ onToken }: { onToken: (token: AdminToken) => void }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    const problem = passwordProblem(current, next, confirm);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onToken(await adminApi.changePassword({ current_password: current, new_password: next }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't change the password. Try again.");
      setBusy(false);
    }
  }

  return (
    <div className="adm-center">
      <Card title="Choose your own password">
        <form className="adm-form" onSubmit={submit} noValidate>
          <p className="tiny muted">
            You signed in with a one-time password. Choose one only you know, at least 12 characters.
          </p>
          {error && (
            <p className="notice notice-crit" role="alert">
              {error}
            </p>
          )}
          <label className="field">
            <span>One-time password</span>
            <input
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              disabled={busy}
            />
          </label>
          <label className="field">
            <span>New password</span>
            <input
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              disabled={busy}
            />
          </label>
          <label className="field">
            <span>New password again</span>
            <input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={busy}
            />
          </label>
          <button className="btn btn-primary btn-block" type="submit" disabled={busy || !current || !next || !confirm}>
            {busy ? "Saving…" : "Save password"}
          </button>
        </form>
      </Card>
    </div>
  );
}

function AddAuthenticator({ onToken }: { onToken: (token: AdminToken) => void }) {
  const [start, setStart] = useState<TotpStart | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Starting rotates the secret on the server, so it happens once per visit to
  // this step, not once per render (or per StrictMode remount).
  const started = useRef(false);

  function begin() {
    setStartError(null);
    adminApi.totpStart().then(setStart, (err: unknown) => {
      started.current = false;
      setStartError(err instanceof Error ? err.message : "Couldn't start. Try again.");
    });
  }

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    begin();
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      onToken(await adminApi.totpConfirm({ code: code.replace(/\s+/g, "") }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't check that code. Try again.");
      setCode("");
      setBusy(false);
    }
  }

  return (
    <div className="adm-center">
      <Card title="Add an authenticator">
        <div className="adm-form">
          <p>
            Add Keystone to an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password…). Scan the
            code, then type the 6 digits it shows.
          </p>

          {startError && (
            <div className="notice notice-crit" role="alert">
              <p>{startError}</p>
              <div className="btn-row" style={{ marginTop: 8 }}>
                <button
                  className="btn"
                  type="button"
                  onClick={() => {
                    started.current = true;
                    begin();
                  }}
                >
                  Try again
                </button>
              </div>
            </div>
          )}

          {start && (
            <>
              <img
                className="adm-qr"
                src={"data:image/svg+xml;charset=utf-8," + encodeURIComponent(start.qr_svg)}
                alt="QR code to scan"
              />
              <div>
                <p className="tiny muted" style={{ marginBottom: 6 }}>
                  Can't scan? Type this key
                </p>
                <div className="adm-secret">{groupSecret(start.secret)}</div>
              </div>
              <a className="btn phone-only adm-link-btn" href={start.otpauth_uri}>
                Open in authenticator app
              </a>
            </>
          )}

          <form className="adm-form" onSubmit={submit} noValidate>
            {error && (
              <p className="notice notice-crit" role="alert">
                {error}
              </p>
            )}
            <label className="field">
              <span>6-digit code</span>
              <input
                className="adm-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={12}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={busy || !start}
              />
            </label>
            <button className="btn btn-primary btn-block" type="submit" disabled={busy || !start || !code.trim()}>
              {busy ? "Checking…" : "Turn it on"}
            </button>
          </form>
        </div>
      </Card>
    </div>
  );
}
